import { Location } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ErrorHandler,
  Injector,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewChild,
  afterNextRender,
  computed,
  inject,
  signal,
} from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { AbstractControl } from '@angular/forms'
import { FieldTree, applyEach, disabled, form, min, readonly, required } from '@angular/forms/signals'
import { MatSnackBar } from '@angular/material/snack-bar'
import { ActivatedRoute, Router } from '@angular/router'

import { arvonMuutokset, asetaArvo } from '../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from '../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from '../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from '../_jaettu-angular/service/lemon-translation.service'

import { LaskuComponentData, LaskuKatseleComponentDataResolve, LaskuKatseleComponentExistingData, LaskutComponentData, LaskutComponentDataResolve } from '../_angular/_resolvers/lasku.resolve'
import { LaskuService } from '../_angular/service/lasku/lasku.service'

import {
  Lasku,
  LaskuBase,
  LaskuReskontra,
  Laskuasetukset,
  LaskunAsiakas,
  LaskunAsiakastyyppi,
  LaskunLahetysdata,
  LaskunLahetystapa,
  LaskunLuonne,
  LaskunTila,
  LaskunTuote,
  LaskunTyypit,
  LaskunTyyppi,
  LaskunumeroTyyppi,
  UUDEN_LASKUN_AVAIN,
} from '@lemon/core-firebase/model/lasku'
import { LaskuChangeNoopCollector, LaskuSharedService } from '@lemon/core-firebase/service/lasku/lasku-shared.service'
import { LaskuKopioija } from '@lemon/core-firebase/service/lasku/lasku.kopioija'
import { TuettuKieli, Valuutta } from '@lemon/core/model/common'
import { CurrencyService } from '@lemon/core/service/currency.service'

import { AreYouSureDialog, AreYouSureDialogData } from '../_jaettu-angular/_components/are-you-sure.dialog'
import { BehaviorSubject, Observable, ReplaySubject, Subject, combineLatest, firstValueFrom, of } from 'rxjs'
import { auditTime, map, pairwise, switchMap, take, takeUntil } from 'rxjs/operators'

import { MatDialog } from '@angular/material/dialog'
import { KayttajanTiedot } from '@lemon/core-firebase/model/kayttaja'
import { LaskuKorkoService } from '@lemon/core-firebase/service/lasku/lasku-korko.service'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { KayttajaService } from 'app/_angular/service/kayttaja.service'
import { InfoDialog } from 'app/_jaettu-angular/_components/info.dialog'
import { LaskuPdfEsikatselutiedot } from '../_jaettu-angular/laskut/esikatselu/pdf.perinteinen.component'
import { laskunAsiakkaanSkeema } from '../_jaettu-angular/laskut/muokkaa/lasku-asiakkaan-tiedot.component'
import { KonfiguraatioPalvelu } from '../_jaettu-angular/service/lasku/configuration.service'
import { LaskuAsiakasDialog, LaskuAsiakasDialogData, LaskuAsiakasDialogTulos } from './dialogit/lasku-asiakas.dialog'
import { onkoAsiakkaanMaaPakollinen, onkoAsiakkaanOsoitePakollinen, onkoMyynninLajiSallittu } from './dialogit/lasku-asiakas-dialog.logic'
import { LaskunLahetystiedot } from './laheta.component'
import { LaskuAsiakasComponent } from './lasku-asiakas.component'
import {
  annaLaskulleKaytettavaTyyppi,
  annaLaskunLuonne,
  annaOletuslahetysvalilehti,
  annaRekisterinMuutostenOtsikonOsat,
  annaRivinRekisterinMuutos,
  annaSivunOtsikonAvain,
  RekisterinMuutoksenTiedot,
  voikoLaskunLuonteenValita,
} from './lasku-muokkaus.logic'
import { annaHinnanSyottotapa, LadattavaLiitetiedosto, LaskuLomake, LaskunRiviLomake, laskunRivinSkeema, liitetiedostonSkeema } from './lasku-rivit.component'
import { KuutionTila } from './kuutio-ikoni.component'
import { annaMaksuaikaVaihtoehdot } from './maksuaika-vaihtoehdot'
import { LaskutusprofiiliService } from '../_angular/service/lasku/laskutusprofiili.service'

interface LaskunTiedotLomake {
  tyyppi: LaskunTyyppi | null
  valuutta: string | null
  maksuaikaValinta: number | 'muu' | null
  maksuaikaa: number | null
}

interface RekisterinMuutos extends RekisterinMuutoksenTiedot {
  rivi: FieldTree<LaskunRiviLomake, number>
}

interface LaskunSummatNakymassa {
  alvitonSumma: number
  alvSumma: number
  yhteensaSumma: number
  alvErittely: { prosentti: number; summa: number }[]
  hyvitysPositiivinenVirhe: boolean
}

@Component({
  selector: 'app-lasku',
  templateUrl: './lasku.component.html',
  styleUrls: ['./lasku.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LaskuComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @ViewChild('asiakkaanTiedot', { read: LaskuAsiakasComponent }) asiakkaanTiedotComponent: LaskuAsiakasComponent

  private ngUnsubscribe = new Subject<void>()
  tilaaEsikatseluPaivitysSubject: ReplaySubject<number> = new ReplaySubject(1)
  aktiivinenValilehti: ReplaySubject<number> = new ReplaySubject(0)
  lahetysdata: ReplaySubject<LaskunLahetysdata> = new ReplaySubject(null)

  laskunTuotteetObservable: ReplaySubject<LaskunTuote[]> = new ReplaySubject(1)
  laskunLiitetiedostotObservable: ReplaySubject<LadattavaLiitetiedosto[]> = new ReplaySubject(1)
  laskunTyyppiObservable: BehaviorSubject<LaskunTyyppi> = new BehaviorSubject(LaskunTyypit.TAVALLINEN)
  naytaLaskunTyyppiObservable: BehaviorSubject<boolean> = new BehaviorSubject(false)
  private _maksuaikaaEnnenKateislaskua: number = null
  readonly laskunEsikatseluVaihtuu = signal(false)
  laskunVastaanottajanMaaObservable: BehaviorSubject<string> = new BehaviorSubject('FIN')
  laskuObservable: ReplaySubject<{ juurilasku: Lasku; kasiteltava: LaskuBase }> = new ReplaySubject(1)
  esikatselutiedot: ReplaySubject<LaskuPdfEsikatselutiedot> = new ReplaySubject(1)
  naytaViivastuskorkoObservable: Observable<boolean>
  valuutatObservable: Observable<Valuutta[]>
  tyypitObservable: Observable<LaskunTyyppi[]>
  sallitutTyypitObservable: Observable<LaskunTyyppi[]>
  laskunTiedotLomake: FieldTree<LaskunTiedotLomake> = null
  paivitaValidaatiot = {}
  hyvitys = false

  lomake: FieldTree<LaskuLomake>
  private readonly _injector = inject(Injector)
  private readonly _changeDetectorRef = inject(ChangeDetectorRef)
  private readonly _laskunLuonnePoissa = signal(false)
  private readonly _tyyppiPoissa = signal(false)
  private readonly _maksuehtoPoissa = signal(false)
  private readonly _laskunTiedotTyyppiPoissa = signal(false)
  private readonly _numeroidaanHyvityksena = signal(false)
  private readonly _asiakastyyppi = signal<LaskunAsiakastyyppi | null>(null)
  readonly myynninLajiEiSovi = signal(false)

  juurilasku: Lasku = null
  kasiteltava: LaskuBase = null
  reskontra: LaskuReskontra[] = null
  asetukset: Laskuasetukset = null
  aiemminMaksettu = 0
  rekisterinMuutoksetAuki = false
  kuutionTilat = KuutionTila

  readonly laskunSummat = signal<LaskunSummatNakymassa>({ alvitonSumma: 0, alvSumma: 0, yhteensaSumma: 0, alvErittely: [], hyvitysPositiivinenVirhe: false })
  readonly rivienMaara = computed(() => this.lomake.tuotteet().value().length)
  readonly rekisterinMuutokset = computed(() => this._annaRekisterinMuutokset())
  readonly rekisterinMuutostenOtsikko = computed(() => this._annaRekisterinMuutostenOtsikko(this.rekisterinMuutokset()))
  readonly maksuaikaVaihtoehdot = computed(() => annaMaksuaikaVaihtoehdot(this.lomake.maksuaikaa().value()))

  asetuksissaOnOletustietojaVirhe = false
  private _poistuminenSallittu = false
  alkuperainenNrotyyppi: LaskunumeroTyyppi = null

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private laskuService: LaskuService,
    private laskuKopioija: LaskuKopioija,
    private validationService: FormValidationService,
    private errorHandler: ErrorHandler,
    private laskuSharedService: LaskuSharedService,
    private currencyService: CurrencyService,
    private ladataanService: LadataanService,
    private snackbar: MatSnackBar,
    private translationService: LemonTranslationService,
    private laskutResolve: LaskutComponentDataResolve,
    private location: Location,
    private laskuKatseleComponentDataResolve: LaskuKatseleComponentDataResolve,
    private konfiguraatioPalvelu: KonfiguraatioPalvelu,
    private _korkoService: LaskuKorkoService,
    private _kayttajaService: KayttajaService,
    private _laskuUriService: LaskuUriService,
    private _lemonaidFirebase: FirebaseLemonaid,
    private _laskutusprofiiliService: LaskutusprofiiliService,
  ) {
    this.juurilasku = this.laskuKopioija.annaUusiLaskuIlmanKorkoprosenttia()
    this.kasiteltava = this.juurilasku
    this.valuutatObservable = this.konfiguraatioPalvelu.valuutatObservable
    this.tyypitObservable = this.konfiguraatioPalvelu.tyypitObservable
    this.sallitutTyypitObservable = combineLatest([this.tyypitObservable, toObservable(this._asiakastyyppi)]).pipe(
      map(([tyypit, asiakastyyppi]) => tyypit.filter(tyyppi => this._sopiikoMyynninLajiAsiakkaalle(tyyppi, asiakastyyppi))),
    )

    const laskunLuonne = annaLaskunLuonne(this.juurilasku)
    const laskunTyyppi: LaskunTyyppi = {
      avain: this.kasiteltava.tyyppi,
    }
    const kasiteltavaEiOleJuurilasku = this.kasiteltava.avain !== this.juurilasku.avain
    this._laskunLuonnePoissa.set(kasiteltavaEiOleJuurilasku)
    this._tyyppiPoissa.set(kasiteltavaEiOleJuurilasku)
    const alkuarvo: LaskuLomake = {
      // Yläosan dropparit
      laskunLuonne,
      ennakkolasku: false,
      tyyppi: laskunTyyppi,
      kieli: this.kasiteltava.kieli ?? null,
      valuutta: this.kasiteltava.valuutta ?? null,
      hinnanSyottotapa: annaHinnanSyottotapa(false),

      // Asiakkaan tiedot
      nimi: null,
      ytunnus: '',
      katuosoite: '',
      maa: '',
      postinro: '',
      postitoimipaikka: '',
      ytunnuksenPakollisuus: null,
      maaPoissa: false,

      // Laskun muut tiedot
      laskunpvm: null,
      erapaiva: null,
      maksuaikaa: null,
      toimituspvm: null,
      lisatiedot: '',
      viitteenne: '',
      viivastyskorko: null,

      // Tuotteet
      tuotteet: [],
      liitetiedostot: [],
    }
    this.lomake = form(signal(alkuarvo), polku => {
      disabled(polku.laskunLuonne, () => this._laskunLuonnePoissa())
      disabled(polku.tyyppi, () => this._tyyppiPoissa())

      laskunAsiakkaanSkeema(polku)
      required(polku.tyyppi)
      required(polku.katuosoite, { when: () => onkoAsiakkaanOsoitePakollinen(this._asiakastyyppi()) })
      required(polku.maa, { when: ({ valueOf }) => onkoAsiakkaanMaaPakollinen(this._asiakastyyppi(), valueOf(polku.tyyppi)) })
      required(polku.postinro, { when: () => onkoAsiakkaanOsoitePakollinen(this._asiakastyyppi()) })
      required(polku.postitoimipaikka, { when: () => onkoAsiakkaanOsoitePakollinen(this._asiakastyyppi()) })

      disabled(polku.laskunpvm)
      readonly(polku.laskunpvm)
      required(polku.erapaiva)
      disabled(polku.erapaiva, () => this._maksuehtoPoissa())
      required(polku.maksuaikaa)
      min(polku.maksuaikaa, 1)
      disabled(polku.maksuaikaa, () => this._maksuehtoPoissa())
      required(polku.viivastyskorko)
      min(polku.viivastyskorko, 0)

      validoiReaktiivisesti(polku.tuotteet, this.vahintaaYksiRiviValidator)
      applyEach(polku.tuotteet, laskunRivinSkeema)
      applyEach(polku.liitetiedostot, liitetiedostonSkeema)
    })

    this.registerForChanges()
  }

  vahintaaYksiRiviValidator = (c: AbstractControl<unknown[]>) => {
    if (c.value.length < 1) {
      return { min: 'true' }
    }
    return null
  }

  ngOnChanges() {
    // console.log('lasku changes')
  }

  ngOnInit() {
    this.naytaViivastuskorkoObservable = combineLatest([this._kayttajaService.kayttajaObservable, this.laskuObservable]).pipe(
      map(([kayttaja, lasku]) => {
        if (lasku?.juurilasku?.avain === lasku.kasiteltava?.avain && kayttaja?.features?.CAN_EDIT_PENALTY_INTEREST === true) {
          return true
        }
        return false
      }),
    )

    arvonMuutokset(this.lomake.tuotteet, { injector: this._injector })
      .pipe(takeUntil(this.ngUnsubscribe), auditTime(50))
      .subscribe(aika => {
        this.laskeSummat()
        this.asetaLaskunLuonneLaskusta(this.juurilasku, this.kasiteltava)
      })

    this.tilaaEsikatseluPaivitysSubject.pipe(takeUntil(this.ngUnsubscribe), auditTime(250)).subscribe(aika => {
      // console.log('tilattu esikatselu paivitys: ', aika)
      if (this.juurilasku && this.kasiteltava && this.kasiteltava.asetukset) {
        this.esikatselutiedot.next({
          juurilasku: this.juurilasku,
          kasiteltava: this.kasiteltava,
          asetukset: this.kasiteltava.asetukset,
        })
      }
    })

    combineLatest([this.route.data, this._kayttajaService.kayttajanTiedotObservable])
      .pipe(
        switchMap(([data, kayttaja]: [{ data: LaskuComponentData }, KayttajanTiedot]) => {
          if (kayttaja?.asiakasAvain && data?.data?.juurilasku?.avain) {
            const laskuReskontraUri = this._laskuUriService.getCustomerLaskunReskontraCollectionUri(kayttaja.asiakasAvain, data.data.juurilasku.avain)
            return this._lemonaidFirebase
              .firestoreCollection<LaskuReskontra>(laskuReskontraUri)
              .listen()
              .pipe(
                map(reskontra => {
                  return [data, reskontra]
                }),
              )
          }
          return of([null, null])
        }),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe({
        next: async ([data, reskontra]: [{ data: LaskuComponentData }, LaskuReskontra[]]) => {
          if (data?.data?.juurilasku?.avain && data?.data?.kasiteltava?.avain && data?.data?.asetukset) {
            await this.alustaNakymaKokonaan(data.data.asetukset, data.data.juurilasku, data.data.kasiteltava, reskontra)
          }
        },
        error: error => {
          this.errorHandler.handleError(error)
        },
      })

    window.scroll({ top: 0, left: 0 })
  }

  private async alustaNakymaKokonaan(asetukset: Laskuasetukset, juurilasku: Lasku, kasiteltava: LaskuBase, reskontra: LaskuReskontra[]) {
    if (!reskontra) {
      return
    }

    this.asetukset = asetukset
    this.juurilasku = juurilasku
    this.kasiteltava = kasiteltava
    this.kasiteltava.asetukset = asetukset
    this.reskontra = reskontra

    this.laskuSharedService.paivitaRyppaanJaKasiteltavanSummat(juurilasku, kasiteltava, reskontra, new LaskuChangeNoopCollector())

    if (!this.kasiteltava.tuotteet) {
      this.kasiteltava.tuotteet = []
    }

    if (!this.kasiteltava.liitteet) {
      this.kasiteltava.liitteet = []
    }

    if (this.juurilasku.viivastyskorkoprosentti === undefined || this.juurilasku.viivastyskorkoprosentti === null) {
      this.juurilasku.viivastyskorkoprosentti = await this._korkoService.annaOletusViivastyskorkoProsenttiNumber()
    }

    this.alkuperainenNrotyyppi = kasiteltava.nrotyyppi
    this._alustaHinnanVerollisuusvalinta()

    this.asetaTyyppiLaskusta(this.juurilasku, this.kasiteltava, true)
    this.laskunTuotteetObservable.next(this.kasiteltava.tuotteet)
    this.laskuObservable.next({ juurilasku: this.juurilasku, kasiteltava: this.kasiteltava })

    // NOTE! THE EXACT ARRAY INSTANCE FROM KASITELTAVA MUST BE PLACED TO THIS
    // ARRAY! IT IS UGLY UGLY, BUT WORKS!
    // NO MAP, NO FILTER, NO NOTHING!
    // The reason for is that the sub component directly modifies the array, which is
    // inside the lasku instance. If we map of filter the array, a new array is created, and
    // the changes are not visible inside the lasku anymore.
    //
    // Loop below marks all already uploaded liitteet as done.
    // The data in lasku is cleaned when saving by the lasku.kopioija, which doesn't identify these
    // properties. (and hence leaves those out of the created copy)
    for (const liite of this.kasiteltava.liitteet) {
      const asLadattava = liite as LadattavaLiitetiedosto
      asLadattava.uploadDone = true
      asLadattava.uploadDonePercentage = 100
    }
    this.laskunLiitetiedostotObservable.next(this.kasiteltava.liitteet)

    if (this.kasiteltava.nrotyyppi === LaskunumeroTyyppi.MUISTUTUS) {
      const summat = this.laskuSharedService.annaLaskunSummatJaTilaLaskulle(this.juurilasku, reskontra)
      const aiemminMaksettuTaiHyvitetty = summat.reskontrasumma.plus(summat.hyvitykset.abs())
      this.aiemminMaksettu = 0 - this.currencyService.muutaBigDecimalRahaksi(aiemminMaksettuTaiHyvitetty)
    } else {
      this.aiemminMaksettu = 0
    }

    if (this.kasiteltava.nrotyyppi === LaskunumeroTyyppi.HYVITYS) {
      this.hyvitys = true
    } else {
      this.hyvitys = false
    }

    // Hae laskunumero
    if (this.kasiteltava.lukossa) {
      // const muotoiltu = this.laskuSharedService.annaMuotoiltuLaskunumero(this.kasiteltava)
      // this.laskuForm.get('laskunro').setValue(muotoiltu)
    } else {
      this.laskuService.haeLaskunumeroArvio(this.kasiteltava).then(numero => {
        this.kasiteltava.nro = numero
        this.tilaaEsikatseluPaivitysSubject.next(1)
      })
    }

    asetaArvo(this.lomake.kieli, this.kasiteltava.kieli ?? null)
    asetaArvo(this.lomake.valuutta, this.kasiteltava.valuutta ?? null)

    this.laskeSummat()

    this.asetaLaskunLuonneLaskusta(this.juurilasku, this.kasiteltava)
    this._changeDetectorRef.markForCheck()

    afterNextRender(() => this.asiakkaanTiedotComponent?.focus(), { injector: this._injector })
  }

  private _alustaHinnanVerollisuusvalinta() {
    const onkoUusiLasku = this.juurilasku.avain === UUDEN_LASKUN_AVAIN
    const riveillaVerollinenHinta = this.kasiteltava.tuotteet.some(tuote => typeof tuote.verollinenHinta === 'number')
    const hinnatVerollisina = riveillaVerollinenHinta || (onkoUusiLasku && !!this.asetukset.laskurivienHinnatVerollisina)
    asetaArvo(this.lomake.hinnanSyottotapa, annaHinnanSyottotapa(hinnatVerollisina), { ilmoita: false })
  }

  ngAfterViewInit() {}

  asetaLaskunLuonneLaskusta(juurilasku: Lasku, kasiteltava: LaskuBase) {
    if (!voikoLaskunLuonteenValita(juurilasku, kasiteltava)) {
      if (this.naytaLaskunTyyppiObservable.value !== false) {
        this.naytaLaskunTyyppiObservable.next(false)
      }
      this._asetaLaskunLuonneJosMuuttui('lasku')
      this._laskunLuonnePoissa.set(true)
      return
    }
    if (this.naytaLaskunTyyppiObservable.value !== true) {
      this.naytaLaskunTyyppiObservable.next(true)
    }
    this._asetaLaskunLuonneJosMuuttui(annaLaskunLuonne(juurilasku))
    this._laskunLuonnePoissa.set(false)
  }

  private _asetaLaskunLuonneJosMuuttui(luonne: LaskunLuonne) {
    const luonneKentta = this.lomake.laskunLuonne
    if (luonneKentta().value() !== luonne) {
      asetaArvo(luonneKentta, luonne)
    }
  }

  private _poistaMuunValuutanRekisterituotteetLaskulta(laskunValuutta: string) {
    const tuotteet = this.kasiteltava.tuotteet || []
    const sailytettavat = tuotteet.filter(laskunTuote => !this._onkoRekisterituoteEriValuutassa(laskunTuote, laskunValuutta))
    const poistettuja = tuotteet.length - sailytettavat.length
    if (poistettuja === 0) {
      return
    }
    this.kasiteltava.tuotteet = sailytettavat
    this.laskunTuotteetObservable.next(sailytettavat)
    const viesti = this.translationService.lokalisoi('lasku.rivit-poistettu-eri-valuutta', { maara: poistettuja, valuutta: laskunValuutta })
    this.snackbar.open(viesti, 'OK', { duration: 7000, verticalPosition: 'top' })
  }

  private _onkoRekisterituoteEriValuutassa(laskunTuote: LaskunTuote, laskunValuutta: string): boolean {
    const tuotteenValuutta = laskunTuote.tuote?.valuutta
    return !!tuotteenValuutta && tuotteenValuutta !== laskunValuutta
  }

  private _haivytaLaskunEsikatseluKunLaskunLuonneVaihtuu() {
    this.laskunEsikatseluVaihtuu.set(true)
    setTimeout(() => this.laskunEsikatseluVaihtuu.set(false), 200)
  }

  get onKateislasku(): boolean {
    return this.lomake.laskunLuonne().value() === 'kateislasku'
  }

  get sivunOtsikonAvain(): string {
    return annaSivunOtsikonAvain({
      luonnettaVoiValita: this.naytaLaskunTyyppiObservable.value,
      onkoTallennettu: this.juurilasku.avain !== UUDEN_LASKUN_AVAIN,
      luonne: this.lomake.laskunLuonne().value(),
    })
  }

  private _lukitseTaiPalautaMaksuehto(luonne: LaskunLuonne) {
    if (luonne === 'kateislasku') {
      if (!this._maksuehtoPoissa()) {
        this._maksuaikaaEnnenKateislaskua = this.lomake.maksuaikaa().value()
        this._maksuehtoPoissa.set(true)
      }
      const laskunpvm = this.lomake.laskunpvm().value()
      if (laskunpvm) {
        asetaArvo(this.lomake.erapaiva, laskunpvm)
      }
      return
    }
    if (this._maksuehtoPoissa()) {
      this._maksuehtoPoissa.set(false)
      asetaArvo(this.lomake.maksuaikaa, this._maksuaikaaEnnenKateislaskua)
    }
  }

  asetaTyyppiLaskusta(juurilasku: Lasku, kasiteltava: LaskuBase, force: boolean) {
    // Set the existing type
    if (kasiteltava.tyyppi != null) {
      this.konfiguraatioPalvelu.tyypitObservable.pipe(take(1)).subscribe(tyypit => {
        const onkoHyvitysTaiKorjaus = kasiteltava.avain !== juurilasku.avain
        const tyyppi = annaLaskulleKaytettavaTyyppi(kasiteltava.tyyppi, onkoHyvitysTaiKorjaus, tyypit)
        if (!tyyppi) {
          return
        }
        asetaArvo(this.lomake.tyyppi, tyyppi)
        if (kasiteltava.avain === juurilasku.avain) {
          this._tyyppiPoissa.set(false)
        } else {
          this._tyyppiPoissa.set(true)
        }
        if (force) {
          this._vaihdaLaskunTyyppi(tyyppi)
        }
      })
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  async voikoLaskultaPoistua(): Promise<boolean> {
    const onkoTallentamattomiaMuutoksia = this.lomake().dirty() && !this._poistuminenSallittu
    if (!onkoTallentamattomiaMuutoksia) {
      return true
    }
    const data: AreYouSureDialogData = {
      header: this.translationService.lokalisoi('lasku.poistuminen.otsikko'),
      text: this.translationService.lokalisoi('lasku.poistuminen.teksti'),
      leftAction: this.translationService.lokalisoi('yleiset.peruuta'),
      rightAction: this.translationService.lokalisoi('lasku.poistuminen.poistu-tallentamatta'),
    }
    const poistutaan = !!(await firstValueFrom(this.dialog.open(AreYouSureDialog, { data }).afterClosed()))
    this._poistuminenSallittu = poistutaan
    return poistutaan
  }

  private laskeSummat() {
    const summat = this.laskuSharedService.annaLaskunSummat(this.kasiteltava)
    const yhteensaSumma = this.currencyService.muutaBigDecimalRahaksi(summat.yhteensaKaikki)
    const alvErittely = summat.alvErittely
      .filter(osa => osa.alvKanta.prosentti > 0)
      .sort((a, b) => b.alvKanta.prosentti - a.alvKanta.prosentti)
      .map(osa => ({ prosentti: osa.alvKanta.prosentti, summa: this.currencyService.muutaBigDecimalRahaksi(osa.alvinSumma) }))
    this.laskunSummat.update(edelliset => ({
      alvitonSumma: this.currencyService.muutaBigDecimalRahaksi(summat.yhteensaIlmanAlv),
      alvSumma: this.currencyService.muutaBigDecimalRahaksi(summat.yhteensaAlv),
      yhteensaSumma,
      alvErittely,
      hyvitysPositiivinenVirhe: yhteensaSumma < 0 ? false : edelliset.hyvitysPositiivinenVirhe,
    }))
    this.kasiteltava.nrotyyppi = yhteensaSumma < 0 ? LaskunumeroTyyppi.HYVITYS : this.alkuperainenNrotyyppi
    this._numeroidaanHyvityksena.set(this.kasiteltava.nrotyyppi === LaskunumeroTyyppi.HYVITYS)
    this.tilaaEsikatseluPaivitysSubject.next(1)
  }

  registerForChanges(): void {
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector })

    muutokset(this.lomake).subscribe(() => {
      this.tilaaEsikatseluPaivitysSubject.next(1)
    })

    muutokset(this.lomake.maa)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(maa => {
        if (this.laskunVastaanottajanMaaObservable.value !== maa) {
          // console.log('maa muuttui', maa, '!==', this.laskunVastaanottajanMaaObservable.value)
          this.laskunVastaanottajanMaaObservable.next(maa)
        }
      })

    muutokset(this.lomake.laskunLuonne)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((value: LaskunLuonne) => {
        if (!value) {
          return
        }
        if (!voikoLaskunLuonteenValita(this.juurilasku, this.kasiteltava)) {
          delete this.juurilasku.ennakkolasku
          delete this.juurilasku.kateislasku
        } else {
          this.juurilasku.ennakkolasku = value === 'ennakkolasku'
          if (value === 'kateislasku') {
            this.juurilasku.kateislasku = true
          } else {
            delete this.juurilasku.kateislasku
          }
        }
        this._lukitseTaiPalautaMaksuehto(value)
        this._haivytaLaskunEsikatseluKunLaskunLuonneVaihtuu()
        this.laskuObservable.next({ juurilasku: this.juurilasku, kasiteltava: this.kasiteltava })
      })

    muutokset(this.lomake.tyyppi)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((value: LaskunTyyppi) => {
        if (!value) {
          return
        }
        const sopiiAsiakkaalle = this._sopiikoMyynninLajiAsiakkaalle(value, this.kasiteltava.asiakas?.asiakastyyppi ?? null)
        if (!sopiiAsiakkaalle) {
          this._tyhjennaMyynninLaji()
          return
        }
        this.myynninLajiEiSovi.set(false)
        if (this.kasiteltava.tyyppi !== value.avain) {
          // console.log('tyyppi muuttui', value?.avain, '!==', this.laskunTyyppiObservable.value?.avain)
          this._vaihdaLaskunTyyppi(value)
        }
      })

    this.laskuObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe(({ kasiteltava }) => {
      this._asiakastyyppi.set(kasiteltava.asiakas?.asiakastyyppi ?? null)
      const tyyppi = this.lomake.tyyppi().value()
      const tyyppiEiSovi = !!tyyppi && !this._sopiikoMyynninLajiAsiakkaalle(tyyppi, this._asiakastyyppi())
      if (tyyppiEiSovi) {
        this._tyhjennaMyynninLaji()
      }
    })

    muutokset(this.lomake.kieli)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(async (value: TuettuKieli) => {
        if (value) {
          this.kasiteltava.kieli = value
        }
      })

    muutokset(this.lomake.valuutta)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((value: string) => {
        if (!value || value === this.kasiteltava.valuutta) {
          return
        }
        this.kasiteltava.valuutta = value
        this._poistaMuunValuutanRekisterituotteetLaskulta(value)
      })

    combineLatest({
      tyyppi: muutokset(this.lomake.tyyppi).pipe(pairwise()),
      asiakas: this._kayttajaService.nykyinenAsiakasObservable,
    })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(({ tyyppi: [edellinenTyyppi, nykyinenTyyppi], asiakas }) => {
        // Indikoi jos suomi.fi -valtuutus puuttuu laskutyypeillä "Yhteisömyynti toiseen EU-maahan (tavara)" ja "Yhteisömyynti toiseen EU-maahan (palvelu)".
        if ((nykyinenTyyppi === LaskunTyypit.EU_PALVELU || nykyinenTyyppi === LaskunTyypit.EU_TAVARA) && asiakas.suomiFiValtuutusPuuttuu) {
          // Palautetaan käyttäjän valinta.
          asetaArvo(this.lomake.tyyppi, edellinenTyyppi)

          const linkki = this._annaTettranLinkki(this.translationService.nykyinenKieli ?? 'fi')
          this.dialog.open(InfoDialog, {
            data: {
              header: this.translationService.lokalisoi('lasku.suomifi-valtuutus-puuttuu-head'),
              text: `
            ${this.translationService.lokalisoi('lasku.suomifi-valtuutus-puuttuu-teksti1')}
            <br>
            <br>
            <a href="${linkki}">${linkki}</a>
            <br>
            <br>
            ${this.translationService.lokalisoi('lasku.suomifi-valtuutus-puuttuu-teksti2')}`,
            },
          })
        }
      })
  }

  private _annaTettranLinkki(kieli: TuettuKieli) {
    if (kieli === 'en') {
      return 'https://app.tettra.co/teams/lemontree/pages/suomifi-authorization'
    }
    return 'https://app.tettra.co/teams/lemontree/pages/suomifi-valtuutuksen-antaminen'
  }

  private _sopiikoMyynninLajiAsiakkaalle(tyyppi: LaskunTyyppi, asiakastyyppi: LaskunAsiakastyyppi | null): boolean {
    const onkoHyvitysTaiKorjaus = this.juurilasku.avain !== this.kasiteltava.avain
    return onkoHyvitysTaiKorjaus || onkoMyynninLajiSallittu(tyyppi, asiakastyyppi)
  }

  private _tyhjennaMyynninLaji() {
    asetaArvo(this.lomake.tyyppi, null)
    this.myynninLajiEiSovi.set(true)
  }

  private _vaihdaLaskunTyyppi(laskunTyyppi: LaskunTyyppi) {
    this.kasiteltava.tyyppi = laskunTyyppi.avain
    this.laskunTyyppiObservable.next(laskunTyyppi)
    this.laskuObservable.next({ juurilasku: this.juurilasku, kasiteltava: this.kasiteltava })
  }

  vertaaTyyppia = (o1: LaskunTyyppi, o2: LaskunTyyppi): boolean => {
    if (o1 && o2) {
      return o1.avain === o2.avain
    }
    return false
  }

  get muokkaaLaskunTietoja(): boolean {
    return !!this.laskunTiedotLomake
  }

  get myyntilajiMuutettu(): boolean {
    const asiakkaanTyyppi = this.kasiteltava?.asiakas?.laskunTyyppi
    return !!asiakkaanTyyppi && this.lomake.tyyppi().value()?.avain !== asiakkaanTyyppi
  }

  get valuuttaMuutettu(): boolean {
    const asiakkaanValuutta = this.kasiteltava?.asiakas?.laskunValuutta
    return !!asiakkaanValuutta && this.lomake.valuutta().value() !== asiakkaanValuutta
  }

  get laskunTiedotMuutettu(): boolean {
    return this.myyntilajiMuutettu || this.valuuttaMuutettu
  }

  aloitaLaskunTietojenMuokkaus() {
    const maksuaikaa = this.lomake.maksuaikaa().value()
    this._laskunTiedotTyyppiPoissa.set(this.lomake.tyyppi().disabled())
    const alkuarvo: LaskunTiedotLomake = {
      tyyppi: this.lomake.tyyppi().value() ?? null,
      valuutta: this.lomake.valuutta().value() ?? null,
      maksuaikaValinta: maksuaikaa,
      maksuaikaa: maksuaikaa,
    }
    this.laskunTiedotLomake = form(
      signal(alkuarvo),
      polku => {
        required(polku.tyyppi)
        disabled(polku.tyyppi, () => this._laskunTiedotTyyppiPoissa())
        required(polku.valuutta)
        required(polku.maksuaikaa)
        min(polku.maksuaikaa, 1)
      },
      { injector: this._injector },
    )
  }

  peruutaLaskunTietojenMuokkaus() {
    this.laskunTiedotLomake = null
  }

  kaytaLaskunTiedotTalleLaskulle() {
    const tiedot = this.laskunTiedotLomake
    const virheitaLomakkeella = this.validationService.onkoLomakkeessaVirheita(tiedot)
    if (virheitaLomakkeella) {
      this.validationService.merkitseKokoLomakeKosketuksi(tiedot)
      return
    }
    const maksuaikaValinta = tiedot.maksuaikaValinta().value()
    const maksuaikaa = maksuaikaValinta === 'muu' ? tiedot.maksuaikaa().value() : maksuaikaValinta
    if (!tiedot.tyyppi().disabled()) {
      asetaArvo(this.lomake.tyyppi, tiedot.tyyppi().value())
    }
    asetaArvo(this.lomake.valuutta, tiedot.valuutta().value())
    if (!this.onKateislasku) {
      asetaArvo(this.lomake.maksuaikaa, maksuaikaa)
    }
    this.lomake().markAsDirty()
    this.laskunTiedotLomake = null
  }

  async avaaAsiakasDialogi(esitaytettyNimi?: string) {
    const muokataanNykyista = !esitaytettyNimi && this.asiakkaanTiedotComponent.naytaKortti()
    const data: LaskuAsiakasDialogData = {
      asiakas: muokataanNykyista ? this.kasiteltava.asiakas : null,
      esitaytettyNimi: esitaytettyNimi ?? '',
      tyyppi: this.lomake.tyyppi().value(),
      tyyppiMuokattavissa: !this.lomake.tyyppi().disabled(),
      valuutta: this.lomake.valuutta().value(),
      kieli: this.lomake.kieli().value(),
    }
    const tulos = await firstValueFrom(this.dialog.open<LaskuAsiakasDialog, LaskuAsiakasDialogData, LaskuAsiakasDialogTulos | 'poistettu'>(LaskuAsiakasDialog, { data, width: '880px' }).afterClosed())
    if (!tulos || tulos === 'poistettu') {
      return
    }
    this.kasiteltava.asiakas.laskunTyyppi = tulos.tyyppi?.avain ?? null
    this.kasiteltava.asiakas.laskunValuutta = tulos.valuutta
    this.kasiteltava.asiakas.laskunKieli = tulos.kieli
    this.kasiteltava.asiakas.oletusLahetystapa = tulos.oletusLahetystapa
    this.kasiteltava.asiakas.maksuaikaa = tulos.maksuaikaa
    this.kasiteltava.asiakas.sahkoinenosoite = tulos.sahkoinenosoite
    this.kasiteltava.asiakas.laskunVastaanottajat = tulos.laskunVastaanottajat
    this.kasiteltava.asiakas.asiakastyyppi = tulos.asiakastyyppi
    this.kasiteltava.asiakas.laskutusprofiiliAvain = tulos.laskutusprofiiliAvain
    await this._asetaLaskutusprofiili(tulos.laskutusprofiiliAvain)
    asetaArvo(this.lomake.tyyppi, tulos.tyyppi)
    asetaArvo(this.lomake.valuutta, tulos.valuutta)
    asetaArvo(this.lomake.kieli, tulos.kieli)
    asetaArvo(this.lomake.nimi, tulos.nimi)
    asetaArvo(this.lomake.ytunnus, tulos.ytunnus ?? '')
    asetaArvo(this.lomake.katuosoite, tulos.katuosoite ?? '')
    asetaArvo(this.lomake.postinro, tulos.postinro ?? '')
    asetaArvo(this.lomake.postitoimipaikka, tulos.postitoimipaikka ?? '')
    asetaArvo(this.lomake.maa, tulos.maa)
    this._asetaAsiakkaanMaksuaika(tulos.maksuaikaa)
    this.lomake().markAsDirty()
    this.asiakkaanTiedotComponent.naytaUudenAsiakkaanKortti()
    this.laskuObservable.next({ juurilasku: this.juurilasku, kasiteltava: this.kasiteltava })
    this._changeDetectorRef.markForCheck()
  }

  async paivitaValittuAsiakas(asiakas: LaskunAsiakas) {
    if (!asiakas) {
      return
    }

    this.kasiteltava.asiakas = asiakas
    await this._asetaLaskutusprofiili(asiakas.laskutusprofiiliAvain)

    // Aseta laskun tyyppi
    if (asiakas.laskunTyyppi && this.kasiteltava.tyyppi !== asiakas.laskunTyyppi) {
      this.kasiteltava.tyyppi = asiakas.laskunTyyppi
      this.asetaTyyppiLaskusta(this.juurilasku, this.kasiteltava, true)
    }

    // Aseta laskun valuutta
    if (asiakas.laskunValuutta && this.kasiteltava.valuutta !== asiakas.laskunValuutta) {
      this.kasiteltava.valuutta = asiakas.laskunValuutta
      asetaArvo(this.lomake.valuutta, this.kasiteltava.valuutta)
    }

    // Aseta laskun kieli
    if (asiakas.laskunKieli && this.kasiteltava.kieli !== asiakas.laskunKieli) {
      this.kasiteltava.kieli = asiakas.laskunKieli
      asetaArvo(this.lomake.kieli, this.kasiteltava.kieli)
    }

    this._asetaAsiakkaanMaksuaika(asiakas.maksuaikaa)
    this.lomake().markAsDirty()

    // Päivitä kaikki.
    this.laskuObservable.next({ juurilasku: this.juurilasku, kasiteltava: this.kasiteltava })
    this._changeDetectorRef.markForCheck()
  }

  private async _asetaLaskutusprofiili(laskutusprofiiliAvain: string | undefined) {
    const onkoHyvitysTaiKorjaus = this.juurilasku.avain !== this.kasiteltava.avain
    if (onkoHyvitysTaiKorjaus) {
      return
    }
    const laskutusprofiili = await this._laskutusprofiiliService.annaLaskutusprofiili(laskutusprofiiliAvain)
    if (!laskutusprofiili) {
      return
    }
    this.asetukset = laskutusprofiili
    this.kasiteltava.asetukset = laskutusprofiili
    this._asetaProfiilinHinnanVerollisuus()
  }

  private _asetaProfiilinHinnanVerollisuus() {
    const onkoUusiLasku = this.juurilasku.avain === UUDEN_LASKUN_AVAIN
    const riveillaOnHintoja = this.lomake
      .tuotteet()
      .value()
      .some(rivi => !!rivi.ahinta)
    if (!onkoUusiLasku || riveillaOnHintoja) {
      return
    }
    asetaArvo(this.lomake.hinnanSyottotapa, annaHinnanSyottotapa(!!this.asetukset.laskurivienHinnatVerollisina), { ilmoita: false })
  }

  private _asetaAsiakkaanMaksuaika(maksuaikaa: number | undefined) {
    const asetettavaMaksuaika = maksuaikaa || this.asetukset?.oletusMaksuaikaa
    if (asetettavaMaksuaika && !this.onKateislasku) {
      asetaArvo(this.lomake.maksuaikaa, asetettavaMaksuaika)
    }
  }

  private _annaYhteensaSentteina() {
    return Math.round((this.laskunSummat().yhteensaSumma + this.aiemminMaksettu) * 100)
  }

  private tarkistaOnkoTiedotOikein(): boolean {
    const hyvitysPositiivinenVirhe = this._annaYhteensaSentteina() >= 0 && this.hyvitys
    this.laskunSummat.update(summat => ({ ...summat, hyvitysPositiivinenVirhe }))
    if (hyvitysPositiivinenVirhe) {
      return false
    }
    const virheitaLomakkeella = this.validationService.onkoLomakkeessaVirheita(this.lomake)
    if (virheitaLomakkeella) {
      this.validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      this.paivitaValidaatiot = {}
      return false
    }
    return true
  }

  async save() {
    this.ladataanService.aloitaLataaminen()
    await this.laskuService
      .tallennaLuonnos(this.juurilasku, this.kasiteltava)
      .then(async ({ juurilasku, kasiteltava }) => {
        this.lomake().reset()
        const lokalisoitu = this.translationService.lokalisoi('lasku.tallennettu-onnistuneesti')
        this.snackbar.open(lokalisoitu, 'OK', { duration: 5000, verticalPosition: 'top' })
        this.location.replaceState('/laskutus/laskut/' + juurilasku.avain + '/' + kasiteltava.avain)
        const data: LaskuKatseleComponentExistingData = {
          juurilasku: juurilasku,
          kasiteltava: kasiteltava,
        }
        this.laskuKatseleComponentDataResolve.asetaOlemassaolevaData(data)
        // Palvelin palauttaa juurilaskun ja käsiteltävän erillisinä olioina, vaikka ne ovat sama lasku. Muokkauksen on
        // osuttava juurilaskuun, koska seuraava tallennus lähettää palvelimelle vain sen.
        const muokattava = this.laskuSharedService.annaKasiteltavaLasku(juurilasku, kasiteltava.avain)
        await this.alustaNakymaKokonaan(this.asetukset, juurilasku, muokattava, this.reskontra)
        this.ladataanService.lopetaLataaminen()
      })
      .catch(err => {
        this.ladataanService.lopetaLataaminen()
        this.errorHandler.handleError(err)
        const virheviesti = this.translationService.lokalisoi('lasku.tallentaminen-epaonnistui')
        this.snackbar.open(virheviesti, 'OK', { duration: 10000, verticalPosition: 'top' })
      })
  }

  vaihdaValilehti(indeksi: number) {
    this.aktiivinenValilehti.next(indeksi)
  }

  peruutaLahettaminen() {
    this.lahetysdata.next(null)
  }

  salliPoistuminenAjastuksenJalkeen() {
    this._poistuminenSallittu = true
  }

  kasitteleLahetys(event: LaskunLahetystiedot) {
    if (!event.error && event.tapa) {
      this._poistuminenSallittu = true
      const olemassaoleva: LaskuKatseleComponentExistingData = {
        juurilasku: event.lahetettyJuuri,
        kasiteltava: event.lahetettyKasiteltava,
      }
      this.laskuKatseleComponentDataResolve.asetaOlemassaolevaData(olemassaoleva)
      if (event.tapa === LaskunLahetystapa.ITSE) {
        const nollaus: LaskutComponentData = {
          nollaaHakuasetukset: true,
        }
        this.laskutResolve.asetaOlemassaolevaData(nollaus)
        const data: LaskuKatseleComponentExistingData = {
          juurilasku: event.lahetettyJuuri,
          kasiteltava: event.lahetettyKasiteltava,
        }
        this.laskuKatseleComponentDataResolve.asetaOlemassaolevaData(data)
        this.router.navigate(['/laskutus/laskut', event.lahetettyJuuri.avain, event.lahetettyKasiteltava.avain, 'tulosta'])
      } else {
        const nollaus: LaskutComponentData = {
          nollaaHakuasetukset: true,
        }
        this.laskutResolve.asetaOlemassaolevaData(nollaus)
        this.router.navigate(['/laskutus/laskut'])
      }
    } else {
      if (event.error) {
        this.ladataanService.naytaVirheDialog(event.error)
      }
    }
  }

  async saveAndSendEmail() {
    if (this.tarkistaOnkoTiedotOikein()) {
      const laskunTyyppi = this.lomake.tyyppi().value()
      if (this.juurilasku.tila === LaskunTila.luonnos && this._annaYhteensaSentteina() < 0 && (laskunTyyppi === LaskunTyypit.EU_PALVELU || laskunTyyppi === LaskunTyypit.EU_TAVARA)) {
        this.dialog.open(InfoDialog, {
          data: {
            text: this.translationService.lokalisoi('lasku.yhteisomyynnit-esta-hyvitys-popup'),
          },
        })
        return
      }

      const data: LaskunLahetysdata = {
        juurilasku: this.juurilasku,
        asetukset: this.asetukset,
        kasiteltava: this.kasiteltava,
        reskontra: this.reskontra,
        huomautuskulu: null,
        korkokulu: null,
        muistutus: false,
      }
      this.lahetysdata.next(data)

      this.aktiivinenValilehti.next(annaOletuslahetysvalilehti(this.kasiteltava.asiakas))

      afterNextRender(
        () => {
          const scrollable = document.querySelector('[cdk-scrollable]')
          if (scrollable) {
            scrollable.scrollTop = 0
          } else {
            window.scrollTo(0, 0)
          }
        },
        { injector: this._injector },
      )
    }
  }

  private _annaRekisterinMuutokset(): RekisterinMuutos[] {
    if (this._numeroidaanHyvityksena()) {
      return []
    }
    const laskunTyyppi = this.lomake.tyyppi().value()?.avain
    const muutokset: RekisterinMuutos[] = []
    for (const rivi of this.lomake.tuotteet) {
      const muutos = annaRivinRekisterinMuutos(rivi().value(), laskunTyyppi)
      if (muutos) {
        muutokset.push({ ...muutos, rivi })
      }
    }
    return muutokset
  }

  private _annaRekisterinMuutostenOtsikko(muutokset: RekisterinMuutos[]): string {
    const osat = annaRekisterinMuutostenOtsikonOsat(muutokset).map(osa => this.translationService.lokalisoi(osa.avain, osa.parametrit))
    return this.translationService.lokalisoi('lasku.rekisteri.lahetysvaiheessa', { muutokset: osat.join(', ') })
  }

  peruRekisterinMuutos(muutos: RekisterinMuutos) {
    const kentta = muutos.rivi.tallennetaanRekisteriin
    asetaArvo(kentta, false)
    kentta().markAsDirty()
  }

  async peruuta() {
    const voidaanPoistua = await this.voikoLaskultaPoistua()
    if (!voidaanPoistua) {
      return
    }
    if (this.juurilasku && this.juurilasku.avain !== UUDEN_LASKUN_AVAIN) {
      const juurilaskunAvain = this.juurilasku.avain
      let kasiteltavanAvain = this.juurilasku.avain

      const liittyvatLaskut = this.laskuSharedService.annaLiittyvatLaskut(this.juurilasku)
      for (const korvaava of liittyvatLaskut) {
        if (korvaava.avain !== UUDEN_LASKUN_AVAIN) {
          kasiteltavanAvain = korvaava.avain
        }
      }

      this.router.navigate(['/laskutus/laskut', juurilaskunAvain, kasiteltavanAvain, 'katsele'])
    } else {
      this.router.navigate(['/laskutus/laskut'])
    }
  }
}
